import React from 'react';
import { useTheme } from '../../../core/ui/ThemeContext';
import { useLanguage } from '../../../core/i18n/LanguageContext';

/**
 * DurchlaufzeitenChart — horizontale CSS-Balken nach Warengruppe.
 * Props:
 *   durchlaufzeiten  { source: string, data: Array<{ warengruppe: string, tage: number }> }
 */
const DurchlaufzeitenChart = ({ durchlaufzeiten }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();

  if (!durchlaufzeiten) return null;

  const data = Array.isArray(durchlaufzeiten.data) ? durchlaufzeiten.data : [];

  const sectionCls = isDarkMode
    ? 'bg-white/6 border border-white/12 rounded-2xl p-5'
    : 'bg-white border border-slate-200 rounded-2xl p-5 shadow-sm';

  const titleCls = isDarkMode
    ? 'text-sm font-semibold text-white/80 mb-4'
    : 'text-sm font-semibold text-slate-700 mb-4';

  const wgCls = isDarkMode ? 'text-xs text-white/70 truncate' : 'text-xs text-slate-600 truncate';
  const tageCls = isDarkMode ? 'text-xs text-white/50 ml-2 flex-shrink-0' : 'text-xs text-slate-400 ml-2 flex-shrink-0';
  const barBg = isDarkMode ? 'bg-blue-400/50' : 'bg-brand/40';

  if (data.length === 0) {
    return (
      <div className={sectionCls}>
        <div className={titleCls}>{t('lm.dashDurchlaufzeiten')}</div>
        <p className={isDarkMode ? 'text-sm text-white/40' : 'text-sm text-slate-400'}>{t('lm.dashEmpty')}</p>
      </div>
    );
  }

  const maxTage = Math.max(...data.map((d) => d.tage), 1);

  return (
    <div className={sectionCls}>
      <div className={titleCls}>{t('lm.dashDurchlaufzeiten')}</div>
      <div className="flex flex-col gap-3">
        {data.map((d, i) => {
          const widthPct = Math.max((d.tage / maxTage) * 100, 2);
          return (
            <div key={i}>
              <div className="flex items-center mb-1">
                <span className={wgCls} style={{ maxWidth: 180 }}>{d.warengruppe}</span>
                <span className={tageCls}>
                  {new Intl.NumberFormat('de-DE', { maximumFractionDigits: 1 }).format(d.tage)} {t('lm.dashDurchlaufDays')}
                </span>
              </div>
              <div className="w-full rounded-full h-2" style={{ background: isDarkMode ? 'rgba(255,255,255,0.08)' : '#e2e8f0' }}>
                <div
                  className={`${barBg} h-2 rounded-full transition-all`}
                  style={{ width: `${widthPct}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DurchlaufzeitenChart;
